/**
 * Per-post comment-thread expansion: what opens by default, and what the reader has since
 * decided for themselves.
 *
 * Two rules, deliberately separated. `defaultThreadExpanded` is a pure function of the post,
 * so it keeps re-deciding as comments arrive; the store only ever holds an EXPLICIT toggle.
 * Persisting the default too would freeze a thread shut the moment it was quiet once.
 */

const STORAGE_KEY_PREFIX = "healo.dashboard.commentThreads.";
// A reader accumulates one entry per post they ever touched; the feed only shows 100. Keep
// the newest few hundred so the value can't grow without bound.
const MAX_STORED_POSTS = 300;

// A long thread renders its last few with "Show N earlier comments" above them; anything at
// or under that limit is already showing in full, so collapsing it would hide nothing but
// cost a click. Reading stays free — only a genuinely long, already-read thread folds away.
export const ALWAYS_EXPANDED_COMMENT_COUNT = 3;

/**
 * Whether a post's thread starts open, absent an explicit choice by this reader.
 *
 * Open when there is something to read that they haven't seen, or when the thread is short
 * enough to show whole. A long thread with nothing new collapses to its summary so a busy
 * announcement can't swallow the feed.
 *
 * `seenSince` is the dashboard-visit stamp (the same one the NEW badges use): undefined means
 * the visit effect hasn't run yet, null means a first-ever visit — treat both as "not read".
 */
export function defaultThreadExpanded({ commentCount = 0, lastCommentAt = "", seenSince } = {}) {
  const count = Number(commentCount) || 0;
  if (count <= 0) return false;
  if (count <= ALWAYS_EXPANDED_COMMENT_COUNT) return true;

  const last = Date.parse(String(lastCommentAt || ""));
  if (!Number.isFinite(last)) return false;
  // null = first-ever visit: everything is unread, so a long thread still opens.
  if (seenSince === null) return true;
  const since = Date.parse(String(seenSince || ""));
  if (!Number.isFinite(since)) return false;
  return last > since;
}

function storageKey(userId) {
  return `${STORAGE_KEY_PREFIX}${String(userId || "anon").trim() || "anon"}`;
}

// localStorage is JSON on every touch, and the feed re-renders on a 120s interval plus every
// realtime ping — so parse once per identity and keep it here.
let cache = { key: "", value: null };

function readMap(userId) {
  const key = storageKey(userId);
  if (cache.key === key && cache.value) return cache.value;
  let parsed = {};
  try {
    const raw = window.localStorage.getItem(key);
    const decoded = raw ? JSON.parse(raw) : null;
    if (decoded && typeof decoded === "object" && !Array.isArray(decoded)) parsed = decoded;
  } catch {
    // Private mode, a quota error or hand-edited junk: fall back to defaults for everything.
    parsed = {};
  }
  cache = { key, value: parsed };
  return parsed;
}

/** The reader's explicit choice for a post, or undefined when they've never toggled it. */
export function readThreadExpanded(userId, postId) {
  const value = readMap(userId)[String(postId || "").trim()];
  return typeof value === "boolean" ? value : undefined;
}

/** Record an explicit toggle. Insertion order carries the eviction order. */
export function writeThreadExpanded(userId, postId, expanded) {
  const id = String(postId || "").trim();
  if (!id) return;
  const current = readMap(userId);
  // Delete first so a re-toggled post moves to the end and survives the trim below.
  delete current[id];
  current[id] = expanded === true;

  const keys = Object.keys(current);
  for (const stale of keys.slice(0, Math.max(0, keys.length - MAX_STORED_POSTS))) {
    delete current[stale];
  }
  cache = { key: storageKey(userId), value: current };
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(current));
  } catch {
    // The in-memory cache still holds it for this session; persistence is a nicety.
  }
}

/** Drop the parsed map so the next read re-parses — used when the signed-in identity changes. */
export function resetThreadExpandedCache() {
  cache = { key: "", value: null };
}
